'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { useGooglePlayUrl } from '@/lib/hooks/useGooglePlayUrl';
import { Button } from '@/lib/ui-kit/button';
import { Icon } from '@/lib/ui-kit/icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/lib/ui-kit/popover';
import { Text } from '@/lib/ui-kit/text';
import { cn } from '@/lib/ui-kit/utils';
import { Button as ButtonV2 } from '@/lib/ui-kit-v2/button';
import { preloadImages } from '@/lib/utils/preloadImage';

interface AppDownloadPopoverProps {
  className?: string;
}

export function AppDownloadPopover({ className }: AppDownloadPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations('nav');
  const googlePlayUrl = useGooglePlayUrl();

  const handlePointerEnter = () =>
    preloadImages(['/outpoll-app-icon.webp', '/google-play.svg'], 'high');

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal={false}>
      <PopoverTrigger asChild>
        {/* Figma 15126:96635: the plain 40px icon button, its 24px glyph in icon/primary hover. */}
        <ButtonV2
          appearance="icon"
          size="M"
          aria-label={t('downloadApp')}
          aria-expanded={isOpen}
          type="button"
          onPointerEnter={handlePointerEnter}
          className={cn('text-v2-icon-primary-hover', className)}
        >
          <Icon name="Download" size={24} />
        </ButtonV2>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={12}
        zIndex={9999}
        className="bg-v2-surface-secondary border-v2-border dark:border-grey-600 flex w-auto flex-col items-center gap-6
          rounded-[10px] border p-5 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
      >
        <div className="flex flex-col items-center gap-3">
          {/* QR code linking to the Google Play listing, with the app icon centered.
              QR stays on a white surface in both themes so it remains scannable. */}
          <div
            className="relative size-[140px] overflow-hidden rounded-[10px] bg-white p-2
              shadow-[0px_4px_6px_-2px_rgba(16,24,40,0.03),0px_12px_16px_-4px_rgba(16,24,40,0.08)]"
          >
            <QRCodeSVG
              value={googlePlayUrl}
              size={124}
              bgColor="#FFFFFF"
              fgColor="#0E1011"
              level="H"
              marginSize={0}
              className="size-full"
            />
            <span
              className="absolute left-1/2 top-1/2 flex size-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center
                rounded-[10px] bg-white"
            >
              <Image
                src="/outpoll-app-icon.webp"
                alt=""
                width={32}
                height={32}
                className="size-8 rounded-[10px]"
              />
            </span>
          </div>
          <Text
            variant="body-medium"
            className="text-v2-text-secondary text-center text-[15px] leading-[26px]"
          >
            {t('scanToDownload')}
          </Text>
        </div>
        <Button
          asChild
          className="bg-v2-btn-primary text-v2-btn-primary-text hover:bg-v2-btn-primary-hover dark:hover:bg-white h-10 w-full
            gap-2 rounded-[10px] px-4 py-3"
        >
          <a href={googlePlayUrl} target="_blank" rel="noopener noreferrer">
            <Image src="/google-play.svg" alt="" width={20} height={20} />
            <Text className="font-[500] text-[13px]">{t('downloadApp')}</Text>
          </a>
        </Button>
      </PopoverContent>
    </Popover>
  );
}
